import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { everyRow, type QuestionsApi, type UploadOutcome } from '../api/questions'
import { useOpenInSimulator } from './openInSimulator'
import { QuestionDrawer } from './QuestionDrawer'
import { QuestionsGrid } from './QuestionsGrid'

/**
 * Questions > Bank: the Question Bank as a grid, and the two ways into a Question.
 *
 * What it is for: extraction writes one YAML file per Question and nothing has ever
 * carried them anywhere. This is the door. Drop a paper's worth of files on it, find the
 * ones worth looking at, and either open one to read and correct it or send several to
 * the Simulator to be held against the paper they came from.
 *
 * The grid is AG Grid, which brings its own filtering, sorting, selection and paging
 * (app:R14). The whole Bank is fetched and filtered in the browser, walked a page of
 * 200 at a time because that is the most the API returns: a thousand rows of summary is
 * nothing to hold, and it makes every filter instant rather than a round trip per
 * keystroke. `ponytail: switch to AG Grid's infinite row model when the Bank outgrows
 * holding whole — the API already filters and pages.`
 */
export function Questions({ api, exam }: { api: QuestionsApi; exam: string }) {
  const [selected, setSelected] = useState<number[]>([])
  const [opened, setOpened] = useState<number | null>(null)

  const query = useQuery({
    queryKey: ['questions', exam],
    queryFn: () => everyRow(api, { exam }),
  })

  const open = useOpenInSimulator((ids: number[]) => api.preview(ids))

  return (
    <>
      {/* Half and half: the heading names the screen, and the one thing you come here to
          do sits beside it rather than under it, where it pushed the table off the fold.
          It stacks on a narrow screen, heading first. */}
      <div className="grid items-start gap-6 lg:grid-cols-2">
        <h2 className="font-heading text-h2 font-bold text-heading">Questions Management</h2>
        <UploadPanel api={api} />
      </div>

      {query.isError && (
        <p role="alert" className="mt-4 border-l-2 border-red-700 pl-3 text-red-700">
          {query.error.message}
        </p>
      )}

      <QuestionsGrid
        rows={query.data ?? []}
        pending={query.isPending}
        onSelectionChanged={setSelected}
        onOpen={setOpened}
      />

      {opened !== null && (
        <QuestionDrawer
          api={api}
          id={opened}
          onClose={() => setOpened(null)}
          onPreview={(id) => open.mutate([id])}
        />
      )}

      <SelectionBar
        selected={selected}
        previewing={open.isPending}
        error={open.error?.message ?? null}
        onPreview={() => open.mutate(selected)}
      />
    </>
  )
}

/**
 * What can be done to a selection, shown only once there is one.
 *
 * A floating bar at the foot of the screen, which is where Midday, ClickUp, Mixpanel and
 * Aboard all put this: it names the count, offers a way out of the selection, and carries
 * the actions. Nothing occupies the screen while nothing is selected, and the action sits
 * with the thing it acts on rather than inside the upload panel it has nothing to do with.
 */
function SelectionBar({
  selected,
  previewing,
  error,
  onPreview,
}: {
  selected: number[]
  previewing: boolean
  error: string | null
  onPreview: () => void
}) {
  if (selected.length === 0) return null

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-center px-4">
      <div
        role="status"
        className="pointer-events-auto flex flex-wrap items-center gap-4 rounded-full border border-ink-100 bg-card px-5 py-3 shadow-lg"
      >
        <span className="font-bold">
          {selected.length} selected
        </span>

        {error && (
          <span role="alert" className="text-red-700">
            {error}
          </span>
        )}

        <button type="button" className={loud} disabled={previewing} onClick={onPreview}>
          <Eye aria-hidden size={16} />
          {previewing ? 'Opening...' : 'View in Simulator'}
        </button>
      </div>
    </div>
  )
}

/**
 * Getting files in, and nothing else.
 *
 * "View in Simulator" is not here: an action on a selection inside a box about dropping
 * files is two unrelated intents sharing one frame. Viewing lives with the selection it
 * acts on, in the bar at the foot of the screen.
 *
 * The whole strip is a drop zone, because what arrives is a folder: `Extracted/` holds one
 * file per Question and dragging it in is fewer moves than a file picker. A zip is taken
 * too — that is what a browser gives you when a folder is compressed rather than opened.
 */
function UploadPanel({ api }: { api: QuestionsApi }) {
  const client = useQueryClient()
  const picker = useRef<HTMLInputElement>(null)
  const [over, setOver] = useState(false)
  const [outcome, setOutcome] = useState<UploadOutcome | null>(null)

  const upload = useMutation({
    mutationFn: (files: File[]) => api.upload(files),
    onSuccess: (result) => {
      setOutcome(result)
      void client.invalidateQueries({ queryKey: ['questions'] })
      void client.invalidateQueries({ queryKey: ['question-topics'] })
    },
  })

  const send = (list: FileList | null) => {
    const files = [...(list ?? [])]
    if (files.length > 0) upload.mutate(files)
  }

  return (
    <section
      aria-label="Upload and preview"
      onDragOver={(event) => {
        event.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault()
        setOver(false)
        send(event.dataTransfer.files)
      }}
      className={`rounded-lg border-2 border-dashed p-4 ${over ? 'border-green-800 bg-green-050' : 'border-ink-100'}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={quiet} onClick={() => picker.current?.click()}>
          <Upload aria-hidden size={16} /> Upload files
        </button>
        <input
          ref={picker}
          type="file"
          multiple
          accept=".yaml,.yml,.json,.zip"
          className="sr-only"
          aria-label="Question files to upload"
          onChange={(event) => send(event.target.files)}
        />

        <span className="text-muted">or drop a folder of .yaml files, or a .zip, here</span>
      </div>

      {upload.isPending && <p className="mt-3 text-muted">Reading the files...</p>}
      {upload.isError && (
        <p role="alert" className="mt-3 text-red-700">
          {upload.error.message}
        </p>
      )}
      {outcome && <UploadReport outcome={outcome} />}
    </section>
  )
}

/**
 * What each file did, named. A folder of ninety is read as a list rather than as one
 * verdict, so the two that were refused can be found without opening every file.
 */
function UploadReport({ outcome }: { outcome: UploadOutcome }) {
  const refused = outcome.files.filter((file) => file.refusals.length > 0)

  return (
    <div className="mt-4" role="status">
      <p className="font-bold">
        {outcome.acceptedCount} stored, {outcome.refusedCount} refused.
      </p>

      {refused.length > 0 && (
        <ul className="mt-2 divide-y divide-ink-100">
          {refused.map((file) => (
            <li key={file.file} className="py-2">
              <span className="font-bold">{file.file}</span>
              <ul className="mt-1 text-red-700">
                {file.refusals.map((refusal, at) => (
                  <li key={at}>
                    <code>{refusal.field}</code> — {refusal.message}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-4 py-2 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'

const loud =
  'inline-flex items-center gap-2 rounded-full bg-green-800 px-5 py-2 font-bold text-white hover:bg-green-900 disabled:opacity-55'
